import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, ProgressBar, Text } from '../../../../components';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useAutoFrame } from '../../../casino-settings/useAutoFrame';
import { DotField } from '../../../customizer/components/DotField';
import { LumioMark } from '../LumioMark';
import type { CreativeItem } from '../../creative-api';
import type { NewLookStatus } from '../../new-look-api';
import { downloadAsset, assetFilename } from '../../download-asset';
import { formatProgressPct } from '../../generation-progress';
import { revealOnLoad } from '../../reveal-media';
import {
  Card,
  CardStack,
  StackLeaf,
  StackLeafBack,
  StackBadge,
  StackCount,
  NewBadge,
  NewLookChip,
  Art,
  ArtImg,
  ArtEmpty,
  Meta,
  Info,
  PlacementLabel,
  Desc,
  ContextRow,
  ContextLeft,
  ContextChip,
  ContextText,
  LinkHint,
  LinkText,
  Actions,
  ActionBtn,
  Origin,
  OriginText,
  EditBtn,
  BusyOverlay,
  BusyInner,
  BusySpark,
  BusyText,
  BusyProgress,
} from './CreativeCard.styles';

/** Layers glyph for the version-stack badge — three stacked cards, currentColor stroke. */
const StackGlyph = ({ size = 12 }: { size?: number }): React.ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
  </svg>
);

/** Download icon (matches Website Settings › Logos): an arrow into a tray, currentColor stroke. */
const DownloadGlyph = ({ size = 14 }: { size?: number }): React.ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 4v10" />
    <path d="M8 10.5l4 4 4-4" />
    <path d="M5 19h14" />
  </svg>
);

export type CreativeCardProps = {
  item: CreativeItem;
  /** Human label for the placement this creative belongs to (the framed/logo card's title). */
  placementLabel: string;
  /** Dim the card while its placement has a run or a pending preview in flight. */
  muted?: boolean;
  /** This exact creative is being updated right now (covers the artwork with a busy state). */
  busy?: boolean;
  /** Label for the busy overlay (already translated); defaults to "Replacing…" (logos). */
  busyLabel?: string;
  /** Real per-format progress of the in-flight multi-slot draw (slots done / total), streamed by the
   *  backend. When set the busy overlay shows a determinate bar + count; absent for fast publish /
   *  replace runs, which keep the plain dot-field. */
  busyProgress?: { completed: number; total: number };
  /** The banner's New look: its art drawn again in the brand's art style by a website regeneration, not live yet. In a
   *  regeneration in place (`redrawing` / `redrawn`) the live banner itself is being redrawn. */
  newLook?: NewLookStatus;
  /**
   * Banners are opaque, full-bleed images: render them edge-to-edge (no auto-toned plate) with
   * their on-site copy as the title/description and where they run + link as context. Logos are
   * transparent marks, so they keep the framed, contained treatment and the placement label.
   */
  plain?: boolean;
  /** The brand's live site (e.g. `https://neonjack.club`) — opens a "view on site" link in a new tab.
   *  Used by logo cards; banner cards omit it (a pool banner may not live on any page). */
  linkBase?: string;
  /** Open the edit flow for this creative. When absent, the card is display-only. */
  onEdit?: () => void;
  /** The card's place in its grid: it enters in the hubs' stagger as its list arrives. */
  enterIndex?: number;
  /** Where the FTUX finds this card (its `data-ftux` id; inert outside an FTUX step). */
  ftuxCard?: FtuxTargetId;
  /** Where the FTUX finds this card's Edit, a banner's button under its copy (inert outside an FTUX step). */
  ftuxEdit?: FtuxTargetId;
};

/**
 * One in-website creative. For a banner (`plain`) the card shows the artwork full-bleed plus the
 * copy that runs on it (headline · subtitle) and where it lives (context · link). For a logo it
 * shows the auto-framed mark and its placement label. Both carry a per-card Edit affordance.
 */
export const CreativeCard = ({
  item,
  placementLabel,
  muted,
  busy,
  busyLabel,
  busyProgress,
  newLook,
  plain,
  linkBase,
  onEdit,
  enterIndex,
  ftuxCard,
  ftuxEdit,
}: CreativeCardProps): React.ReactElement => {
  const { t } = useTranslation();
  // Determinate per-format progress for an in-flight multi-slot draw; publish/replace runs pass none.
  const genPct = busyProgress ? formatProgressPct(busyProgress.completed, busyProgress.total) : 0;
  const origin: 'lumio' | 'custom' = item.origin === 'custom' ? 'custom' : 'lumio';
  const name = item.headline || item.internal_name || placementLabel;
  // A redrawn banner shows no chip: its own art is the new art. One with a published version keeps Redrawing until
  // the regeneration ends: its card shows that version until then.
  const chip = newLook === 'redrawn' && item.published_version_id ? 'redrawing' : newLook;
  // Auto-tone the plate per artwork (only for framed logos; banners fill the card).
  const frame = useAutoFrame(item.image_url ?? '', 'dark');
  // The brand's live site, so the owner can open it and see the banner running. Needs the site base;
  // without it there's nothing to open.
  const siteHref = linkBase ? linkBase.replace(/\/$/, '') || linkBase : undefined;
  const download = (): void => {
    if (item.image_url) void downloadAsset(item.image_url, assetFilename(placementLabel, name, item.image_url));
  };

  // Banner cards are clickable wholes that default to Edit; the inner controls (Download, Edit,
  // link) stop propagation so they keep their own action instead of also opening the editor.
  const cardClickable = Boolean(plain && onEdit && !busy);
  const onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!cardClickable || !onEdit) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEdit();
    }
  };
  const stop = (event: React.MouseEvent): void => event.stopPropagation();

  // A banner with regenerations reads as a subtle stack (a couple of offset layers behind) plus a
  // "N versions" badge. Only for full-bleed banners, not framed logos.
  const versionCount = item.version_count ?? item.versions?.length ?? 1;
  const stacked = Boolean(plain && versionCount > 1);
  // Stable per-card seed so the busy dot-field never drifts in lockstep across simultaneous runs.
  const busySeed = Array.from(item.banner_id).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);

  const originChip = (
    <Origin $origin={origin}>
      {origin === 'custom' ? <Icon name="edit" size={11} /> : <LumioMark size={12} />}
      <OriginText text={origin === 'custom' ? 'Custom' : 'Lumio'} />
    </Origin>
  );

  const card = (
    <Card
      $enterIndex={stacked ? undefined : enterIndex}
      $muted={muted}
      $clickable={cardClickable}
      onClick={cardClickable && onEdit ? () => onEdit() : undefined}
      onKeyDown={cardClickable ? onCardKeyDown : undefined}
      role={cardClickable ? 'button' : undefined}
      tabIndex={cardClickable ? 0 : undefined}
      aria-label={cardClickable ? t('Edit {{name}} creative', { name }) : undefined}
      data-ftux={ftuxCard}
    >
      <Art $plain={plain} $frame={plain ? undefined : item.image_url ? frame : undefined}>
        {item.image_url ? (
          <ArtImg
            key={item.image_url}
            {...revealOnLoad}
            $cover={plain}
            src={item.image_url}
            alt={t('{{name}} creative', { name })}
            loading="lazy"
          />
        ) : (
          <ArtEmpty aria-hidden="true">
            <Icon name="eye" size={20} />
          </ArtEmpty>
        )}
        {/* Logos keep the overlay Edit affordance; banners move actions below so nothing covers
            the artwork. */}
        {!plain && onEdit && !busy ? (
          <EditBtn type="button" onClick={onEdit} aria-label={t('Edit {{name}} creative', { name })}>
            <Icon name="edit" size={13} />
            <span>{t('Edit')}</span>
          </EditBtn>
        ) : null}
        {busy ? (
          <BusyOverlay aria-live="polite">
            <DotField seed={busySeed} />
            <BusyInner>
              <BusySpark src="/assets/acq/lumio-spark.svg" alt="" aria-hidden="true" />
              {busyProgress ? (
                <BusyText
                  text="{{done}} of {{total}} formats"
                  placeholders={{ done: busyProgress.completed, total: busyProgress.total }}
                />
              ) : busyLabel ? (
                <BusyText text={busyLabel} />
              ) : (
                <BusyText text="Replacing…" />
              )}
            </BusyInner>
            {busyProgress ? (
              <BusyProgress role="progressbar" aria-valuenow={genPct} aria-valuemin={0} aria-valuemax={100}>
                <ProgressBar value={genPct} animate />
              </BusyProgress>
            ) : null}
          </BusyOverlay>
        ) : null}
        {stacked ? (
          <StackBadge aria-label={t('{{n}} versions', { n: versionCount })}>
            <StackGlyph size={12} />
            <StackCount>{versionCount}</StackCount>
          </StackBadge>
        ) : null}
        {plain && chip && chip !== 'redrawn' && !busy ? (
          <NewLookChip $failed={chip === 'failed'}>
            {chip === 'failed' ? null : <LumioMark size={11} />}
            {chip === 'drawing' ? (
              <Text tag="span" text="Drawing new look…" />
            ) : chip === 'redrawing' ? (
              <Text tag="span" text="Redrawing…" />
            ) : chip === 'ready' ? (
              <Text tag="span" text="New look ready" />
            ) : (
              <Text tag="span" text="New look failed" />
            )}
          </NewLookChip>
        ) : null}
        {plain && item.has_new && !busy ? (
          <NewBadge aria-label={t('A new version is ready')}>
            <Text tag="span" text="New" />
          </NewBadge>
        ) : null}
      </Art>

      {plain ? (
        <Info>
          {/* Eyebrow: where the banner runs (category) on the left, who authored it on the right,
              sitting ABOVE the headline. */}
          <ContextRow>
            <ContextLeft>
              {item.context ? (
                <ContextChip>
                  <ContextText text={item.context} />
                </ContextChip>
              ) : null}
            </ContextLeft>
            {originChip}
          </ContextRow>
          <PlacementLabel text={name} />
          {item.subtitle ? <Desc text={item.subtitle} /> : null}
          {siteHref ? (
            <LinkHint href={siteHref} target="_blank" rel="noreferrer" title={t('Open on your site')} onClick={stop}>
              <Icon name="external-link" size={11} />
              <LinkText text={t('View on site')} />
            </LinkHint>
          ) : null}
          <Actions>
            {item.image_url ? (
              <ActionBtn
                type="button"
                onClick={(event) => {
                  stop(event);
                  download();
                }}
                aria-label={t('Download {{name}}', { name })}
              >
                <DownloadGlyph size={14} />
                <span>{t('Download')}</span>
              </ActionBtn>
            ) : null}
            {onEdit ? (
              <ActionBtn
                type="button"
                onClick={(event) => {
                  stop(event);
                  onEdit();
                }}
                aria-label={t('Edit {{name}} creative', { name })}
                data-ftux={ftuxEdit}
              >
                <Icon name="edit" size={13} />
                <span>{t('Edit')}</span>
              </ActionBtn>
            ) : null}
          </Actions>
        </Info>
      ) : (
        <Meta>
          <PlacementLabel text={placementLabel} />
          {originChip}
        </Meta>
      )}
    </Card>
  );

  // A stacked banner gets two lighter cards peeking out beneath the front one, so it clearly reads as
  // a stack (a staircase of offsets) rather than one card with a thick border.
  if (!stacked) return card;
  return (
    <CardStack $enterIndex={enterIndex}>
      <StackLeafBack aria-hidden="true" />
      <StackLeaf aria-hidden="true" />
      {card}
    </CardStack>
  );
};

export default CreativeCard;
